import { getCurrency } from '@common/utils/currency.ts'
import type { TooltipOptions } from 'chart.js'
import type { _DeepPartialObject } from 'chart.js/dist/types/utils'
import { format, parseISO } from 'date-fns'

import { formatMinutesAndHours, formatSecondsAndMinutes } from '@/utils/helpers'

export const ChartFonts = (isDark?: boolean, isMobile?: boolean) => {
  return {
    titleFont: {
      family: 'Inter',
      weight: 500,
      size: 16,
    },
    bodyFont: {
      family: 'Inter',
      weight: 500,
      size: isMobile ? 12 : 14,
    },
    color: isDark ? '#fff' : '#3B4056',
  }
}

export const ChartColors = [
  '#0AB8BC',
  '#EF358E',
  '#9601CA',
  '#1E84E3',
  '#C067DF',
  '#FF903F',
  '#FAD02E',
  '#7EBD3B',
  '#D8A7B1',
  '#4E5D94',
  '#00A896',
  '#FFAA5B',
  '#38A1DB',
  '#A85FF4',
  '#FF57B1',
  '#00845B',
  '#FFD700',
  '#8C6B94',
  '#FFB6C1',
  '#007BFF',
  '#FF6961',
  '#83D0C9',
  '#FF8C42',
  '#5E6A71',
  '#FFD369',
  '#FF6B6B',
]

export const gridLineColor = (isDark?: boolean) => {
  return { color: isDark ? '#3B4056' : '#EEEEEE' }
}

export const customToolTip:
  | _DeepPartialObject<TooltipOptions<'doughnut' | 'line' | 'bar'>>
  | undefined = {
  titleFont: ChartFonts().titleFont,
  titleColor: '#000',
  bodyFont: ChartFonts().bodyFont,
  bodyColor: '#898C9A',
  bodyAlign: 'left',
  backgroundColor: '#fff',
  displayColors: false,
  usePointStyle: false,
}

export const dateLabels = (labels: unknown[]) => {
  let labelsItems

  try {
    const firstYear = parseISO(labels[0] as string).getFullYear()
    const isCurrentYear =
      firstYear ===
        parseISO(labels[labels.length - 1] as string).getFullYear() &&
      firstYear === new Date().getFullYear()

    labelsItems = labels.map((date) =>
      format(parseISO(date as string), isCurrentYear ? 'MMM dd' : 'MMM dd, yy'),
    )
  } catch (error) {
    labelsItems = labels
  }

  return {
    labels: labelsItems,
  }
}

export const multipleLines = (
  isCurrency?: boolean,
  showCents = false,
):
  | _DeepPartialObject<TooltipOptions<'doughnut' | 'line' | 'bar'>>
  | undefined => {
  return {
    callbacks: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      label: function (item: any) {
        const value = item.dataset.data[item.dataIndex] as number
        const valueText = isCurrency
          ? getCurrency(value, showCents ? 2 : 0)
          : value

        return `${valueText} - ${item.dataset.label}`
      },
    },
  }
}
export const getTooltipCurrency = (
  showCents?: boolean,
):
  | _DeepPartialObject<TooltipOptions<'doughnut' | 'line' | 'bar'>>
  | undefined => ({
  callbacks: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    label: function (item: any) {
      return getCurrency(
        item.dataset.data[item.dataIndex] as number,
        showCents ? 2 : 0,
      )
    },
  },
})

export const tooltipHoursFromMinutes:
  | _DeepPartialObject<TooltipOptions<'doughnut' | 'line' | 'bar'>>
  | undefined = {
  callbacks: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    label: function (item: any) {
      const totalMinutes = item.dataset.data[item.dataIndex]

      return formatMinutesAndHours(totalMinutes)
    },
  },
}

export const tooltipMinutesFromSeconds:
  | _DeepPartialObject<TooltipOptions<'doughnut' | 'line' | 'bar'>>
  | undefined = {
  callbacks: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    label: function (item: any) {
      const totalSeconds = item.dataset.data[item.dataIndex]

      return formatSecondsAndMinutes(totalSeconds)
    },
  },
}

export const tooltipHideBody:
  | _DeepPartialObject<TooltipOptions<'doughnut' | 'line' | 'bar'>>
  | undefined = {
  callbacks: {
    label: () => '',
  },
}

export const getAxisCurrency = (showCents = false) => ({
  callback: (tick: string | number) => {
    return getCurrency(tick, showCents ? 2 : 0)
  },
})

export const getAxisFixedDecimal = (fixedDecimal = 0) => ({
  callback: (tick: string | number) => {
    return Number(tick).toFixed(fixedDecimal)
  },
})
